/**
 * Passkey (WebAuthn) auth. Works on web, iOS, and Android.
 * Uses react-native-passkeys for all platforms (browser APIs on web, native on iOS/Android).
 * Compatible with our SimpleWebAuthn backend.
 */
import * as Passkeys from 'react-native-passkeys';

export type PublicKeyCredentialCreationOptionsJSON = Parameters<typeof Passkeys.create>[0];
export type PublicKeyCredentialRequestOptionsJSON = Parameters<typeof Passkeys.get>[0];

export function isPasskeySupported(): boolean {
  return Passkeys.isSupported();
}

export async function registerPasskey(
  options: PublicKeyCredentialCreationOptionsJSON
): Promise<NonNullable<Awaited<ReturnType<typeof Passkeys.create>>>> {
  const controller = new AbortController();
  const response = await Passkeys.create({
    ...stripCredProps(options),
    signal: controller.signal,
  });
  if (!response) {
    throw new Error('Passkey registration was cancelled or failed');
  }
  return response;
}

/**
 * SimpleWebAuthn always injects `extensions.credProps: true` into registration
 * options. On web, react-native-passkeys pops an `alert()` for every requested
 * extension the browser doesn't echo back in `getClientExtensionResults()`, so
 * a missing credProps result surfaces as a spurious
 * "Webauthn extension credProps is undefined" dialog. We never read the
 * credProps result — `residentKey: 'required'` already guarantees a
 * discoverable credential at verification — so drop it before handing the
 * options to the authenticator. Other extensions (if any) are preserved.
 */
function stripCredProps(
  options: PublicKeyCredentialCreationOptionsJSON
): PublicKeyCredentialCreationOptionsJSON {
  if (!options.extensions || !('credProps' in options.extensions)) {
    return options;
  }
  const { credProps: _credProps, ...rest } = options.extensions;
  const { extensions: _extensions, ...base } = options;
  // Re-add `extensions` only when something other than credProps remains, so we
  // never hand back an explicit `extensions: undefined` (exactOptionalPropertyTypes).
  return Object.keys(rest).length > 0 ? { ...base, extensions: rest } : base;
}

export async function authenticateWithPasskey(
  options: PublicKeyCredentialRequestOptionsJSON
): Promise<NonNullable<Awaited<ReturnType<typeof Passkeys.get>>>> {
  const response = await Passkeys.get(options);
  if (!response) {
    throw new Error('Passkey sign-in was cancelled or failed');
  }
  return response;
}
